import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Loader2, Gift, Plus, Ticket, ShieldAlert } from 'lucide-react';

const TYPE_META = {
  points: { label: 'Points', icon: Plus, color: 'text-blue-400' },
  free_booking: { label: 'Comped Booking', icon: Gift, color: 'text-green-400' },
  discount_code: { label: 'Discount Code', icon: Ticket, color: 'text-amber-400' },
};

export default function AdminCompensation() {
  const [search, setSearch] = useState('');
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['compensation-logs', search],
    queryFn: () => base44.entities.CompensationLog.list('-created_date', 200),
  });

  const filtered = search
    ? logs.filter((l) =>
        (l.customer_email || '').toLowerCase().includes(search.toLowerCase()) ||
        (l.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (l.reference || '').toLowerCase().includes(search.toLowerCase())
      )
    : logs;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-white/5">
        <div className="w-10 h-10 border border-primary/20 bg-primary/5 flex items-center justify-center">
          <ShieldAlert className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-white font-black text-xl leading-tight">Compensation History</h1>
          <p className="text-white/40 text-xs">Audit trail of all manual points, bookings, and discount codes issued by staff.</p>
        </div>
      </div>

      {/* Search */}
      <input
        className="w-full bg-white/5 border border-white/10 text-white h-11 px-3 placeholder:text-white/20 focus:border-primary/60 transition-all"
        placeholder="Search by customer email, name, or reference…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-white/30 text-sm">No compensation records found.</div>
      ) : (
        <div className="overflow-x-auto border border-white/5">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-white/5 text-white/40 text-[10px] font-bold tracking-widest uppercase">
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Admin</th>
                <th className="text-left px-4 py-3">Details</th>
                <th className="text-left px-4 py-3">Reason</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((log) => {
                const meta = TYPE_META[log.action_type] || {};
                const Icon = meta.icon;
                return (
                  <tr key={log.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-white/50 whitespace-nowrap">
                      {new Date(log.created_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 ${meta.color} font-bold`}>
                        {Icon && <Icon className="w-3 h-3" />}
                        {meta.label || log.action_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/70">
                      <p className="font-medium">{log.customer_name || '—'}</p>
                      <p className="text-white/30 text-[10px]">{log.customer_email}</p>
                    </td>
                    <td className="px-4 py-3 text-white/50">{log.admin_email}</td>
                    <td className="px-4 py-3 text-white/60 max-w-xs">
                      <p>{log.details}</p>
                      {log.reference && <p className="text-white/30 text-[10px] font-mono mt-0.5">{log.reference}</p>}
                    </td>
                    <td className="px-4 py-3 text-white/50 max-w-xs">{log.reason}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}